import { apiClient } from '../../../../core/api/client';

/**
 * Represents a network connection from the generic /network endpoint
 * Includes both Service Provider and Financial Advisor connections
 */
export type NetworkConnection = {
  id: string;
  initiatorUserId: string;
  targetUserId: string;
  isMutual: boolean;
  createdAt: string;
  target: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: 'SERVICE_PROVIDER' | 'FINANCIAL_ADVISOR';
    serviceProvider?: {
      id: string;
      companyName: string;
      businessAddress: string;
      categories: Array<{
        category: {
          id: string;
          name: string;
        };
      }>;
      specialties: Array<{
        specialty: {
          id: string;
          name: string;
        };
      }>;
    };
    financialAdvisor?: {
      id: string;
      companyName: string;
    };
  };
};

/**
 * Normalized professional data for dashboard display
 * Supports both Service Providers and Financial Advisors
 */
export type NetworkProfessional = {
  id: string;
  targetUserId: string;
  role: 'SERVICE_PROVIDER' | 'FINANCIAL_ADVISOR';
  name: string;
  companyName: string;
  specialty: string | null;
  profilePhoto: string | null;
  addedAt: string;
  providerId?: string; // Service Provider ID (only for SERVICE_PROVIDER role)
};

export type NetworkProfessionalsResponse = {
  data: NetworkProfessional[];
  total: number;
  hasMore: boolean;
};

// Legacy type alias for backward compatibility
export type NetworkProvider = NetworkProfessional;
export type NetworkProvidersResponse = NetworkProfessionalsResponse;

/**
 * Transform raw network connection to normalized professional data
 */
const transformConnection = (conn: NetworkConnection): NetworkProfessional => {
  const { target, createdAt, id, targetUserId } = conn;
  const fullName = `${target.firstName} ${target.lastName}`.trim();
  
  if (target.role === 'SERVICE_PROVIDER' && target.serviceProvider) {
    const primarySpecialty = target.serviceProvider.specialties?.[0]?.specialty?.name || null;
    return {
      id,
      targetUserId,
      role: 'SERVICE_PROVIDER',
      name: fullName,
      companyName: target.serviceProvider.companyName,
      specialty: primarySpecialty,
      profilePhoto: null, // Will be handled separately via S3
      addedAt: createdAt,
      providerId: target.serviceProvider.id, // Store the actual service provider ID
    };
  }

  // Financial Advisor
  return {
    id,
    targetUserId,
    role: 'FINANCIAL_ADVISOR',
    name: fullName,
    companyName: target.financialAdvisor?.companyName || fullName,
    specialty: null, // FAs don't have specialties
    profilePhoto: null, // Will be handled separately via S3
    addedAt: createdAt,
  };
};

/**
 * Fetch list of professionals (SP and FA) in the Financial Advisor's network for dashboard display
 * Returns up to 10 most recently added professionals with pagination metadata
 *
 * @returns Network professionals response with data, total count, and hasMore flag
 * @throws Error if request fails
 */
export async function fetchNetworkProfessionals(): Promise<NetworkProfessionalsResponse> {
  // Use the generic /network endpoint which returns both SP and FA connections
  const response = await apiClient.get<NetworkConnection[]>('/network');
  const connections = response.data;
  
  // Filter to only professional connections (exclude any Care Seeker connections that might exist)
  const professionalConnections = connections.filter(
    conn => conn.target.role === 'SERVICE_PROVIDER' || conn.target.role === 'FINANCIAL_ADVISOR'
  );
  
  // Transform to normalized format
  const professionals = professionalConnections.map(transformConnection);
  
  // Return dashboard-optimized response (limit to 10, add metadata)
  const dashboardLimit = 10;
  const data = professionals.slice(0, dashboardLimit);
  
  return {
    data,
    total: professionals.length,
    hasMore: professionals.length > dashboardLimit,
  };
}

/**
 * Legacy function alias for backward compatibility
 * @deprecated Use fetchNetworkProfessionals instead
 */
export const fetchNetworkProviders = fetchNetworkProfessionals;
